import React, { useState, useEffect, useRef } from "react"
import {
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Button,
  Spinner,
} from "reactstrap"
import { useSelector } from "react-redux"

const truncateText = (text, maxLength) => {
  return text?.length > maxLength ? `${text.substring(0, maxLength)}...` : text
}

const formatDate = dateString => {
  const options = { year: "numeric", month: "short", day: "numeric" }
  return new Date(dateString).toLocaleDateString(undefined, options)
}

const PostsList = props => {
  const { posts = [], loading, setPostClicked, setSelectedPost } = props
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(5)
  const mode = useSelector(state => state.Layout.layoutMode)
  const containerRef = useRef(null)

  // Pagination logic
  const totalPages = Math.ceil(posts.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentPosts = posts.slice(startIndex, endIndex)

  const paginate = pageNumber => setCurrentPage(pageNumber)

  return (
    <div
      style={{
        padding: "0px 20px",
        borderRadius: "20px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: mode === "dark" ? "#252f40" : "#fff",
        marginBottom: "20px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "20px 20px 0 20px",
        }}
      >
        <h4
          style={{
            fontWeight: "600",
            fontSize: "16px",
            color: mode === "dark" ? "#9ca3af" : "#374151",
            margin: 0,
          }}
        >
          Posts
        </h4>
      </div>

      {/* Table */}
      <Row>
        <Col xl={12}>
          <div
            ref={containerRef}
            style={{
              backgroundColor: mode === "dark" ? "#252f40" : "#fff",
              overflowY: "auto",
              maxHeight: "400px",
              padding: "20px",
            }}
          >
            {/* Table Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "150px 150px 1fr 150px 150px",
                gap: "16px",
                padding: "16px 20px",
                backgroundColor: mode === "dark" ? "#1a2332" : "#f9fafb",
                borderBottom: `1px solid ${
                  mode === "dark" ? "#3a4556" : "#e5e7eb"
                }`,
                fontWeight: "600",
                fontSize: "14px",
                color: mode === "dark" ? "#9ca3af" : "#374151",
              }}
            >
              <div className="text-center">Post ID</div>
              <div className="text-center">Posted On</div>
              <div className="text-center">Caption</div>
              <div className="text-center">Media Type</div>
              <div className="text-center">View Details</div>
            </div>

            {/* Table Body */}
            {loading ? (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                }}
              >
                <Spinner
                  color="primary"
                  style={{ height: "3rem", width: "3rem" }}
                />
              </div>
            ) : currentPosts.length > 0 ? (
              currentPosts.map((post, index) => (
                <div
                  key={index}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "150px 150px 1fr 150px 150px",
                    gap: "16px",
                    padding: "16px 20px",
                    borderBottom:
                      index < currentPosts.length - 1
                        ? `1px solid ${mode === "dark" ? "#3a4556" : "#e5e7eb"}`
                        : "none",
                    backgroundColor: mode === "dark" ? "#252f40" : "#fff",
                    color: mode === "dark" ? "#d1d5db" : "#374151",
                    fontSize: "14px",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor =
                      mode === "dark" ? "#2d3748" : "#f9fafb"
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor =
                      mode === "dark" ? "#252f40" : "#fff"
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    {truncateText(post.id, 8)}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    {formatDate(post.timestamp)}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    {truncateText(post.caption || "No Caption", 50)}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    {post.media_type || "Image"}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <Button
                      size="sm"
                      onClick={() => {
                        setPostClicked(true)
                        setSelectedPost(post)
                      }}
                      style={{
                        cursor: "pointer",
                        backgroundColor: "#2990DF", 
                        border: "none",
                        borderRadius: "6px",
                        padding: "4px 10px",
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  color: mode === "dark" ? "#9ca3af" : "#6b7280",
                }}
              >
                No posts available
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "left",
          padding: "16px",
        }}
      >
        <Pagination className="mb-0">
          <PaginationItem disabled={currentPage === 1}>
            <PaginationLink
              previous
              onClick={() => paginate(currentPage - 1)}
              style={{
                color: mode === "dark" ? "#fff" : "#000",
                backgroundColor: "transparent",
                borderColor: mode === "dark" ? "#444" : "#dee2e6",
              }}
            />
          </PaginationItem>

          {Array.from({ length: totalPages }).map((_, i) => (
            <PaginationItem active={i + 1 === currentPage} key={i}>
              <PaginationLink
                onClick={() => paginate(i + 1)}
                style={{
                  borderColor:
                    i + 1 === currentPage
                      ? "#2990DF"
                      : mode === "dark"
                        ? "#444"
                        : "#dee2e6",
                  backgroundColor:
                    i + 1 === currentPage ? "#2990DF" : "transparent",
                  color:
                    i + 1 === currentPage
                      ? "#fff"
                      : mode === "dark"
                        ? "#fff"
                        : "#000",
                  boxShadow: "none",
                }}
              >
                {i + 1}
              </PaginationLink>
            </PaginationItem>
          ))}

          <PaginationItem disabled={currentPage === totalPages}>
            <PaginationLink
              next
              onClick={() => paginate(currentPage + 1)}
              style={{
                color: mode === "dark" ? "#fff" : "#000",
                backgroundColor: "transparent",
                borderColor: mode === "dark" ? "#444" : "#dee2e6",
              }}
            />
          </PaginationItem>
        </Pagination>
      </div>
    </div>
  )
}

export default PostsList
